'use client';

import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
  OutlinedInput,
  Button,
  CircularProgress,
  Stack,
  LinearProgress,
  TableContainer,
  TablePagination,
  Menu
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useNavigate } from 'react-router-dom';
import ExcelJS from 'exceljs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import StorageIcon from '@mui/icons-material/Storage';
import { useMasterVehicleList } from '../../stores/masterVehicleStore';
import MasterVehicleAddEdit from '../master-data/vehicle/MasterVehicleAddEdit';
import MasterCompanyAddEdit from '../master-data/company/MasterCompanyAddEdit';
import { useCompanyDataList } from '../../stores/companyStore';
import { useNotifier } from '../../components/Notifier';
import PickerField from '../../components/PickerField';
import usePickerPagination from '../../hooks/usePickerPagination';
import { useLanguage } from '../../utils/i18n';
import { timezone } from '../../utils/timeZone';
import { getAllSensorReport } from '../vehicle-fuel-history/constant';

const headerStyle = {
  backgroundColor: '#5166AE',
  color: 'white',
  border: '1px solid #ddd',
  padding: '12px 8px',
  textAlign: 'center',
  fontWeight: 'bold',
  whiteSpace: 'nowrap'
};

const cellStyle = {
  border: '1px solid #ddd',
  padding: '8px',
  whiteSpace: 'nowrap',
  textAlign: 'center',
  fontSize: '0.875rem'
};

const formatNum = (val) => {
  if (typeof val === 'number') return val.toFixed(2);
  return val ?? '-';
};

const timeToSeconds = (timeStr) => {
  if (!timeStr) return 0;
  const parts = timeStr.split(':');
  if (parts.length === 3) {
    return parseInt(parts[0], 10) * 3600 + parseInt(parts[1], 10) * 60 + parseInt(parts[2], 10);
  }
  return 0;
};

const secondsToTime = (totalSeconds) => {
  if (totalSeconds == null || isNaN(totalSeconds)) return '-';
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const aggregateVehicleData = (dailyData) => {
  if (!dailyData || dailyData.length === 0) return null;
  if (dailyData.length === 1) {
    return { ...dailyData[0], details: dailyData };
  }

  const result = {
    vehicle: dailyData.find((d) => d.vehicle != null)?.vehicle,
    details: dailyData,
    start_time: dailyData.find((d) => d.start_time != null)?.start_time,
    end_time: [...dailyData].reverse().find((d) => d.end_time != null)?.end_time,
    start_odo: dailyData.find((d) => d.start_odo != null && d.start_odo !== 0)?.start_odo,
    end_odo: [...dailyData].reverse().find((d) => d.end_odo != null && d.end_odo !== 0)?.end_odo,
    total_distance: 0,
    duration_sec: 0,
    duration_engine_idle_sec: 0,
    duration_engine_on_sec: 0,
    duration_engine_off_sec: 0,
    total_over_speed: 0,
    max_speed: 0,
    avg_speed_sum: 0,
    avg_speed_count: 0,
    fuel_tank: dailyData.find((d) => d.fuel_tank != null && d.fuel_tank !== 0)?.fuel_tank,
    fuel_consumption_by_ratio_percentage: 0,
    fuel_consumption_by_ratio_litre: 0,
    fuel_start_percentage: dailyData.find((d) => d.fuel_start_percentage != null && d.fuel_start_percentage !== 0)?.fuel_start_percentage,
    fuel_start_litre: dailyData.find((d) => d.fuel_start_litre != null && d.fuel_start_litre !== 0)?.fuel_start_litre,
    fuel_end_percentage: [...dailyData].reverse().find((d) => d.fuel_end_percentage != null && d.fuel_end_percentage !== 0)
      ?.fuel_end_percentage,
    fuel_end_litre: [...dailyData].reverse().find((d) => d.fuel_end_litre != null && d.fuel_end_litre !== 0)?.fuel_end_litre,
    refuel_percentage: 0,
    refuel_litre: 0,
    fuel_consumption_percentage: 0,
    fuel_consumption_litre: 0,

    canbus_fuel_start_percentage: dailyData.find((d) => d.canbus_fuel_start_percentage != null && d.canbus_fuel_start_percentage !== 0)
      ?.canbus_fuel_start_percentage,
    canbus_fuel_start_litre: dailyData.find((d) => d.canbus_fuel_start_litre != null && d.canbus_fuel_start_litre !== 0)
      ?.canbus_fuel_start_litre,
    canbus_fuel_end_percentage: [...dailyData]
      .reverse()
      .find((d) => d.canbus_fuel_end_percentage != null && d.canbus_fuel_end_percentage !== 0)?.canbus_fuel_end_percentage,
    canbus_fuel_end_litre: [...dailyData].reverse().find((d) => d.canbus_fuel_end_litre != null && d.canbus_fuel_end_litre !== 0)
      ?.canbus_fuel_end_litre,
    canbus_refuel_percentage: 0,
    canbus_refuel_litre: 0,
    canbus_fuel_consumption_percentage: 0,
    canbus_fuel_consumption_litre: 0,

    can_engine_worktime_sec: 0,
    ratio_litre_to_km: dailyData.find((d) => d.ratio_litre_to_km != null && d.ratio_litre_to_km !== 0)?.ratio_litre_to_km
  };

  dailyData.forEach((d) => {
    result.total_distance += d.total_distance || 0;
    result.duration_sec += timeToSeconds(d.duration);
    result.duration_engine_idle_sec += timeToSeconds(d.duration_engine_idle);
    result.duration_engine_on_sec += timeToSeconds(d.duration_engine_on);
    result.duration_engine_off_sec += timeToSeconds(d.duration_engine_off);
    result.total_over_speed += d.total_over_speed || 0;

    if (d.max_speed > result.max_speed) result.max_speed = d.max_speed;

    if (d.avg_speed > 0) {
      result.avg_speed_sum += d.avg_speed;
      result.avg_speed_count += 1;
    }

    result.fuel_consumption_by_ratio_percentage += d.fuel_consumption_by_ratio_percentage || 0;
    result.fuel_consumption_by_ratio_litre += d.fuel_consumption_by_ratio_litre || 0;

    result.refuel_percentage += d.refuel_percentage || 0;
    result.refuel_litre += d.refuel_litre || 0;
    result.fuel_consumption_percentage += d.fuel_consumption_percentage || 0;
    result.fuel_consumption_litre += d.fuel_consumption_litre || 0;

    result.canbus_refuel_percentage += d.canbus_refuel_percentage || 0;
    result.canbus_refuel_litre += d.canbus_refuel_litre || 0;
    result.canbus_fuel_consumption_percentage += d.canbus_fuel_consumption_percentage || 0;
    result.canbus_fuel_consumption_litre += d.canbus_fuel_consumption_litre || 0;

    result.can_engine_worktime_sec += timeToSeconds(d.can_engine_worktime);
  });

  result.duration = secondsToTime(result.duration_sec);
  result.duration_engine_idle = secondsToTime(result.duration_engine_idle_sec);
  result.duration_engine_on = secondsToTime(result.duration_engine_on_sec);
  result.duration_engine_off = secondsToTime(result.duration_engine_off_sec);
  result.can_engine_worktime = secondsToTime(result.can_engine_worktime_sec);

  result.avg_speed = result.avg_speed_count > 0 ? result.avg_speed_sum / result.avg_speed_count : 0;

  return result;
};

const getCompanyIdFromStorage = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const stored = localStorage.getItem('companyId');
      if (stored && String(stored).toLowerCase() !== 'all') {
        return String(stored);
      }
    }
  } catch (err) {
    console.warn('cannot read localStorage', err);
  }
  return '';
};

export default function ReportAllSensorPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const notify = useNotifier();

  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('');
  const [reportData, setReportData] = useState([]);
  const [generationTimeRange, setGenerationTimeRange] = useState(false);
  const [displayStartDate, setDisplayStartDate] = useState('');
  const [displayEndDate, setDisplayEndDate] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  // Filters State
  const [periodType, setPeriodType] = useState('today');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [dateError, setDateError] = useState('');

  // Export Menu State
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const openExportMenu = Boolean(exportAnchorEl);

  useEffect(() => {
    if (!periodType) return;
    if (periodType === 'custom' && fromDate && toDate) return;

    const now = dayjs();
    if (periodType === 'custom') {
      if (!fromDate) {
        setFromDate('');
        setToDate('');
      }
      return;
    }

    let from;
    let to = now;

    if (periodType === 'today') {
      from = now.startOf('day');
    } else if (periodType === 'week') {
      from = now.subtract(7, 'day').startOf('day');
      to = now.endOf('day');
    } else if (periodType === 'month') {
      from = now.subtract(1, 'month').startOf('day');
      to = now.endOf('day');
    } else {
      return;
    }

    if (from && to) {
      setFromDate(from.format('YYYY-MM-DD HH:mm:ss'));
      setToDate(to.format('YYYY-MM-DD HH:mm:ss'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodType]);

  const [selectedCompanyIds, setSelectedCompanyIds] = useState(() => {
    const defaultId = getCompanyIdFromStorage();
    return defaultId ? [defaultId] : [];
  });
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const selectedVehicleIds = useMemo(() => selectedVehicles.map((v) => String(v.value)), [selectedVehicles]);

  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });
  const companyOptions = companyPickerParams.options;

  const selectedCompaniesParam = useMemo(() => {
    if (selectedCompanyIds.length === 0 || selectedCompanyIds.includes('all')) {
      return companyOptions.map((c) => ({ value: c.value }));
    }
    return selectedCompanyIds.map((id) => ({ value: id }));
  }, [selectedCompanyIds, companyOptions]);

  const vehiclePickerParams = usePickerPagination({
    fetchHook: useMasterVehicleList,
    buildArgs: (state) => [state.page, state.pageSize, selectedCompaniesParam, state.searchKey, state.searchValue],
    formatOption: (p) => ({
      value: p.id,
      label: p.license_plate,
      original: p
    }),
    searchKey: 'license_plate',
    companyId: selectedCompaniesParam
  });
  const vehicleList = vehiclePickerParams.options;

  const vehicleOptionsNormalized = useMemo(
    () =>
      (vehicleList || []).map((c) => ({
        value: String(c.id ?? c.vehicle_id ?? c.value ?? c),
        label: c.original?.license_plate ?? c.license_plate ?? c.label ?? c.name ?? String(c.id ?? ''),
        companyName:
          c.original?.join?.company_name ||
          c.original?.company_name ||
          c.join?.company_name ||
          c.company_name ||
          c.companyName ||
          t('reportModule.unknownCompany', 'Unknown Company'),
        raw: c.raw ?? c
      })),
    [vehicleList, t]
  );

  const validateDateRange = (start, end) => {
    if (!start || !end) return false;
    const diffMinutes = end.diff(start, 'minute');
    if (diffMinutes > 31 * 24 * 60) {
      setDateError(t('reportModule.errMaxRange31Days', 'Maximum time span of 31 days'));
      return false;
    } else if (diffMinutes < 0) {
      setDateError(t('reports.errStartTimeLater', 'Waktu awal tidak boleh melebihi waktu akhir'));
      return false;
    }
    setDateError('');
    return true;
  };

  const handleFetchData = async () => {
    if (selectedCompanyIds.length === 0) {
      notify({ message: t('reports.errSelectCompany', 'Please select a company'), severity: 'warning' });
      return;
    }
    if (selectedVehicleIds.length === 0) {
      notify({ message: t('reports.errSelectVehicle', 'Please select a vehicle'), severity: 'warning' });
      return;
    }
    if (!fromDate || !toDate || dateError) {
      notify({ message: t('reports.errSelectValidDateRange', 'Please select a valid date range'), severity: 'warning' });
      return;
    }

    setLoading(true);
    setReportData([]);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setLoadingProgress(0);
    setLoadingText(t('reportModule.preparingData', 'Preparing data...'));
    try {
      let apiChunks = [];
      const localStart = dayjs(fromDate);
      const localEnd = dayjs(toDate);
      const pType = periodType;
      const isFullDayPeriod = ['week', 'last_week', 'month', 'last_month'].includes(pType);

      if (isFullDayPeriod) {
        let curr = localStart.startOf('day');
        const end = localEnd.startOf('day');
        while (curr.isBefore(end) || curr.isSame(end, 'day')) {
          apiChunks.push({
            startLocal: curr.startOf('day'),
            endLocal: curr.endOf('day')
          });
          curr = curr.add(1, 'day');
        }
      } else {
        let curr = localStart.startOf('day');
        const end = localEnd.startOf('day');

        while (curr.isBefore(end) || curr.isSame(end, 'day')) {
          let chunkStart = curr.startOf('day');
          let chunkEnd = curr.endOf('day');

          if (curr.isSame(localStart, 'day')) chunkStart = localStart;
          if (curr.isSame(localEnd, 'day')) chunkEnd = localEnd;

          apiChunks.push({
            startLocal: chunkStart,
            endLocal: chunkEnd
          });

          curr = curr.add(1, 'day');
        }
      }

      const allData = [];
      const totalVehicles = selectedVehicleIds.length;
      let completedVehicles = 0;
      const chunkSize = 5;

      for (let i = 0; i < totalVehicles; i += chunkSize) {
        const chunk = selectedVehicleIds.slice(i, i + chunkSize);
        const chunkResults = await Promise.all(
          chunk.map(async (vId) => {
            const vInfo =
              selectedVehicles.find((o) => String(o.value) === String(vId)) ||
              vehicleOptionsNormalized.find((o) => String(o.value) === String(vId));
            const vehicleCompanyId =
              vInfo?.raw?.company || vInfo?.original?.company_id || vInfo?.original?.company || selectedCompanyIds[0] || 0;
            const licensePlate = vInfo?.label || String(vId);

            let vehicleDailyData = [];

            const tz = typeof timezone === 'function' ? timezone() : Number(timezone) || 7;
            for (const chunk of apiChunks) {
              const dStartIso = chunk.startLocal.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]');
              const dEndIso = chunk.endLocal.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]');

              try {
                const payload = {
                  company_id: vehicleCompanyId,
                  vehicle_id: vId,
                  start_date: dStartIso,
                  end_date: dEndIso,
                  tz
                };
                const res = await getAllSensorReport(payload);
                let dataToPush = [];
                if (res?.data?.data) {
                  dataToPush = Array.isArray(res.data.data) ? res.data.data : [res.data.data];
                } else if (res?.data) {
                  dataToPush = Array.isArray(res.data) ? res.data : [res.data];
                }

                dataToPush = dataToPush.map((d) => ({
                  ...d,
                  chunk_start: chunk.startLocal.format(),
                  chunk_end: chunk.endLocal.format()
                }));

                vehicleDailyData = vehicleDailyData.concat(dataToPush);
              } catch (err) {
                console.error(`Error fetching data for ${licensePlate} on ${chunk.startLocal.format()}`, err);
              }
            }

            completedVehicles++;
            setLoadingProgress(Math.round((completedVehicles / totalVehicles) * 100));
            setLoadingText(
              `${t('reportModule.processingUnit', 'Processing unit:')} ${licensePlate} (${completedVehicles}/${totalVehicles})`
            );

            const validDailyData = vehicleDailyData.filter((d) => d && (d.vehicle || d.start_time || d.chunk_start));
            const aggregated = aggregateVehicleData(validDailyData);
            return aggregated ? [aggregated] : [];
          })
        );
        allData.push(...chunkResults.flat());
      }

      if (allData && allData.length > 0) {
        // filter out invalid data
        setReportData(allData.filter((d) => d && (d.vehicle || d.start_time || d.details)));
        setGenerationTimeRange(true);
        setDisplayStartDate(localStart.format('DD/MM/YYYY HH:mm:ss'));
        setDisplayEndDate(localEnd.format('DD/MM/YYYY HH:mm:ss'));
        notify({ message: t('reports.dataFetchedSuccess', 'Data fetched successfully'), severity: 'success' });
      } else {
        setReportData([]);
        setGenerationTimeRange(false);
        notify({ message: t('reports.noDataFound', 'No data found for the selected period'), severity: 'info' });
      }
    } catch (error) {
      console.error(error);
      notify({ message: t('reports.errFetchData', 'Failed to fetch data'), severity: 'error' });
      setReportData([]);
      setGenerationTimeRange(false);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (isCsv = false) => {
    if (!reportData || reportData.length === 0) {
      notify({ message: t('reports.noDataExport', 'Tidak ada data untuk diexport'), severity: 'warning' });
      return;
    }
    try {
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet(t('reportModule.allSensorReport', 'All Sensor Report'));

      if (isCsv) {
        const csvHeaders = [
          t('reports.licensePlate', 'License Plate'),
          t('reportModule.travelTimeStart', 'Travel Time Start'),
          t('reportModule.travelTimeEnd', 'Travel Time End'),
          t('reportModule.distanceKm', 'Distance (km)'),
          t('reports.duration', 'Duration'),
          t('reportModule.idle', 'Idle'),
          t('reportModule.engineOn', 'Engine ON'),
          t('reportModule.engineOff', 'Engine OFF'),
          t('reportModule.canbusEngineWorkTime', 'Canbus Engine Work Time'),
          t('reportModule.speedOver', 'Speed Over'),
          t('reportModule.speedMax', 'Speed Max'),
          t('reportModule.speedAvg', 'Speed Avg'),
          t('reportModule.fuelTank', 'Fuel Tank'),
          t('reportModule.ratioLKm', 'Ratio (L:km)'),
          t('reportModule.consRatioPct', 'Cons Ratio %'),
          t('reportModule.consRatioLitre', 'Cons Ratio Litre'),
          t('reportModule.fsStartPct', 'FS Start %'),
          t('reportModule.fsStartLitre', 'FS Start Litre'),
          t('reportModule.fsEndPct', 'FS End %'),
          t('reportModule.fsEndLitre', 'FS End Litre'),
          t('reportModule.fsRefuelPct', 'FS Refuel %'),
          t('reportModule.fsRefuelLitre', 'FS Refuel Litre'),
          t('reportModule.fsConsPct', 'FS Cons %'),
          t('reportModule.fsConsLitre', 'FS Cons Litre'),
          t('reportModule.cfStartPct', 'CF Start %'),
          t('reportModule.cfStartLitre', 'CF Start Litre'),
          t('reportModule.cfEndPct', 'CF End %'),
          t('reportModule.cfEndLitre', 'CF End Litre'),
          t('reportModule.cfRefuelPct', 'CF Refuel %'),
          t('reportModule.cfRefuelLitre', 'CF Refuel Litre'),
          t('reportModule.cfConsPct', 'CF Cons %'),
          t('reportModule.cfConsLitre', 'CF Cons Litre')
        ];
        sheet.addRow(csvHeaders);
      } else {
        const excelHeaders1 = [
          t('reports.licensePlate', 'License Plate'),
          t('reportModule.travelTime', 'Travel Time'),
          '',
          '',
          '',
          t('reportModule.idle', 'Idle'),
          t('exportWaypoint.engine', 'Engine'),
          '',
          t('reportModule.canbusEngineWorkTimeHhMm', 'Canbus Engine Work Time (hh:mm:ss)'),
          t('reports.speed', 'Speed'),
          '',
          '',
          t('reportModule.fuelTank', 'Fuel Tank'),
          t('reportModule.ratioLKm', 'Ratio (L:km)'),
          t('reportModule.fuelConsumptionByRatio', 'Fuel Consumption By Ratio'),
          '',
          t('reportModule.fuelSensor', 'Fuel Sensor'),
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          t('reportModule.canbusFuel', 'Canbus Fuel'),
          '',
          '',
          '',
          '',
          '',
          '',
          ''
        ];
        const excelHeaders2 = [
          '',
          t('time.start', 'Start'),
          t('time.end', 'End'),
          t('reportModule.distanceKm', 'Distance (km)'),
          t('reportModule.durationHhMmSs', 'Duration (hh:mm:ss)'),
          '',
          t('reportModule.onHhMmSs', 'ON (hh:mm:ss)'),
          t('reportModule.offHhMmSs', 'OFF (hh:mm:ss)'),
          '',
          t('reportModule.overTime', 'Over (time)'),
          t('reportModule.maxKmH', 'Max (km/h)'),
          t('reportModule.avgKmH', 'Avg (km/h)'),
          '',
          '',
          '%',
          t('reportModule.litre', 'Litre'),
          t('time.start', 'Start'),
          '',
          t('time.end', 'End'),
          '',
          t('dailyFuel.refueling', 'Refueling'),
          '',
          t('reportModule.consumption', 'Consumption'),
          '',
          t('time.start', 'Start'),
          '',
          t('time.end', 'End'),
          '',
          t('dailyFuel.refueling', 'Refueling'),
          '',
          t('reportModule.consumption', 'Consumption'),
          ''
        ];
        const excelHeaders3 = [
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          '%',
          t('reportModule.litre', 'Litre'),
          '%',
          t('reportModule.litre', 'Litre'),
          '%',
          t('reportModule.litre', 'Litre'),
          '%',
          t('reportModule.litre', 'Litre'),
          '%',
          t('reportModule.litre', 'Litre'),
          '%',
          t('reportModule.litre', 'Litre'),
          '%',
          t('reportModule.litre', 'Litre'),
          '%',
          t('reportModule.litre', 'Litre')
        ];
        sheet.addRow(excelHeaders1);
        sheet.addRow(excelHeaders2);
        sheet.addRow(excelHeaders3);

        sheet.mergeCells(1, 1, 3, 1);
        sheet.mergeCells(1, 2, 1, 5);
        sheet.mergeCells(2, 2, 3, 2);
        sheet.mergeCells(2, 3, 3, 3);
        sheet.mergeCells(2, 4, 3, 4);
        sheet.mergeCells(2, 5, 3, 5);
        sheet.mergeCells(1, 6, 3, 6);
        sheet.mergeCells(1, 7, 1, 8);
        sheet.mergeCells(2, 7, 3, 7);
        sheet.mergeCells(2, 8, 3, 8);
        sheet.mergeCells(1, 9, 3, 9);
        sheet.mergeCells(1, 10, 1, 12);
        sheet.mergeCells(2, 10, 3, 10);
        sheet.mergeCells(2, 11, 3, 11);
        sheet.mergeCells(2, 12, 3, 12);
        sheet.mergeCells(1, 13, 3, 13);
        sheet.mergeCells(1, 14, 3, 14);
        sheet.mergeCells(1, 15, 1, 16);
        sheet.mergeCells(2, 15, 3, 15);
        sheet.mergeCells(2, 16, 3, 16);
        sheet.mergeCells(1, 17, 1, 24);
        sheet.mergeCells(2, 17, 2, 18);
        sheet.mergeCells(2, 19, 2, 20);
        sheet.mergeCells(2, 21, 2, 22);
        sheet.mergeCells(2, 23, 2, 24);
        sheet.mergeCells(1, 25, 1, 32);
        sheet.mergeCells(2, 25, 2, 26);
        sheet.mergeCells(2, 27, 2, 28);
        sheet.mergeCells(2, 29, 2, 30);
        sheet.mergeCells(2, 31, 2, 32);

        [1, 2, 3].forEach((rowIdx) => {
          const row = sheet.getRow(rowIdx);
          row.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF5166AE' } };
            cell.alignment = { vertical: 'middle', horizontal: 'center' };
            cell.border = {
              top: { style: 'thin' },
              left: { style: 'thin' },
              bottom: { style: 'thin' },
              right: { style: 'thin' }
            };
          });
        });
      }

      reportData.forEach((row) => {
        const addedRow = sheet.addRow([
          row?.vehicle?.license_plate || '-',
          row?.start_odo ? formatNum(row.start_odo / 1000) : '-',
          row?.end_odo ? formatNum(row.end_odo / 1000) : '-',
          formatNum(row?.total_distance),
          row?.duration ?? '-',
          row?.duration_engine_idle ?? '-',
          row?.duration_engine_on ?? '-',
          row?.duration_engine_off ?? '-',
          row?.can_engine_worktime ?? '-',
          row?.total_over_speed,
          formatNum(row?.max_speed),
          formatNum(row?.avg_speed),
          row?.fuel_tank,
          row?.ratio_litre_to_km,
          formatNum(row?.fuel_consumption_by_ratio_percentage),
          formatNum(row?.fuel_consumption_by_ratio_litre),
          formatNum(row?.fuel_start_percentage),
          formatNum(row?.fuel_start_litre),
          formatNum(row?.fuel_end_percentage),
          formatNum(row?.fuel_end_litre),
          formatNum(row?.refuel_percentage),
          formatNum(row?.refuel_litre),
          formatNum(row?.fuel_consumption_percentage),
          formatNum(row?.fuel_consumption_litre),
          formatNum(row?.canbus_fuel_start_percentage),
          formatNum(row?.canbus_fuel_start_litre),
          formatNum(row?.canbus_fuel_end_percentage),
          formatNum(row?.canbus_fuel_end_litre),
          formatNum(row?.canbus_refuel_percentage),
          formatNum(row?.canbus_refuel_litre),
          formatNum(row?.canbus_fuel_consumption_percentage),
          formatNum(row?.canbus_fuel_consumption_litre)
        ]);

        if (!isCsv) {
          addedRow.eachCell({ includeEmpty: true }, (cell) => {
            cell.alignment = { vertical: 'middle', horizontal: 'left' };
            cell.border = {
              top: { style: 'thin' },
              left: { style: 'thin' },
              bottom: { style: 'thin' },
              right: { style: 'thin' }
            };
          });
        }
      });

      if (!isCsv) {
        sheet.columns.forEach((column) => {
          let maxLen = 10;
          column.eachCell({ includeEmpty: true }, (cell) => {
            if (cell.value) {
              const valLen = cell.value.toString().length;
              if (valLen > maxLen) maxLen = valLen;
            }
          });
          column.width = Math.min(maxLen + 2, 50);
        });
      }

      const buffer = isCsv ? await workbook.csv.writeBuffer() : await workbook.xlsx.writeBuffer();
      const blobType = isCsv ? 'text/csv;charset=utf-8;' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([buffer], { type: blobType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `All_Sensor_Report_${dayjs().format('YYYYMMDD_HHmmss')}.${isCsv ? 'csv' : 'xlsx'}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      notify({ message: t('reports.errExportExcel', 'Failed to export'), severity: 'error' });
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
        <Box sx={{ mb: 1, flexShrink: 0 }}>
          <Typography variant="h4" fontWeight={700} display="flex" alignItems="center" gap={1}>
            <StorageIcon fontSize="large" color="primary" />
            {t('reportModule.allSensorReport', 'All Sensor Report')}
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 1,
            flexShrink: 0,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            background: 'linear-gradient(to right bottom, #ffffff, #fcfcfc)'
          }}
        >
          <Box
            sx={{
              p: 1,
              px: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
              bgcolor: 'rgba(25, 118, 210, 0.04)',
              borderRadius: 2
            }}
          >
            <Typography variant="body2" color="#1976d2" fontWeight="500" sx={{ lineHeight: 1.5 }}>
              {t(
                'reportModule.vehicleOperationalDataFromAllSensors',
                'Vehicle operational data from all sensors, including engine status, speed, and fuel consumption.'
              )}
            </Typography>
            {(localStorage.getItem('rolename') === 'Administrator' ||
              (() => {
                try {
                  return JSON.stringify(JSON.parse(localStorage.getItem('master_menu') || '[]')).includes(
                    '"modules_path":"report-catalog"'
                  );
                } catch {
                  return false;
                }
              })()) && (
              <Box>
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/report-catalog?name=report-catalog')}
                  size="small"
                  variant="contained"
                  sx={{
                    bgcolor: 'primary',
                    color: '#ffffff',
                    textTransform: 'none',
                    px: 0.5,
                    py: 0,
                    minWidth: 0,
                    '&:hover': { bgcolor: '#36477c', color: '#ffffff' }
                  }}
                >
                  {t('reports.reportCatalog', 'Report Catalog')}
                </Button>
              </Box>
            )}
          </Box>
          <Box display="flex" flexWrap="wrap" gap={2.5} alignItems="center">
            <Box sx={{ flex: '1 1 200px' }}>
              <PickerField
                label={t('general.company', 'Company')}
                value={selectedCompanyIds}
                {...companyPickerParams}
                options={companyOptions}
                onChange={(ids) => {
                  setSelectedCompanyIds(ids || []);
                  setSelectedVehicles([]);
                }}
                allowAdd
                AddComponent={MasterCompanyAddEdit}
                dialogTitle={t('general.selectCompany', 'Select Company')}
                multiSelect={true}
              />
            </Box>
            <Box sx={{ flex: '1 1 250px' }}>
              <PickerField
                label={t('reportModule.selectVehicle', 'Select Vehicle *')}
                value={selectedVehicleIds}
                {...vehiclePickerParams}
                options={vehicleOptionsNormalized}
                onChange={(ids) => {
                  if (!ids || ids.length === 0) {
                    setSelectedVehicles([]);
                    return;
                  }
                  const newSelected = ids.map((id) => {
                    const strId = String(id);
                    let match = vehicleOptionsNormalized.find((v) => String(v.value) === strId);
                    if (!match) match = selectedVehicles.find((v) => String(v.value) === strId);

                    if (!match) {
                      const rawMatch =
                        (vehicleList || []).find((v) => String(v.value) === strId) ||
                        (vehiclePickerParams.listOptions || []).find((v) => String(v.value) === strId);
                      if (rawMatch) {
                        match = {
                          value: strId,
                          label: rawMatch.raw?.license_plate ?? rawMatch.original?.license_plate ?? rawMatch.label ?? strId,
                          companyName: rawMatch.companyName,
                          raw: rawMatch.raw
                        };
                      } else {
                        match = { value: strId, label: strId };
                      }
                    }
                    return match;
                  });
                  setSelectedVehicles(newSelected);
                }}
                dialogTitle={t('reports.selectVehicle', 'Select Vehicle')}
                multiSelect={true}
                allowAdd
                AddComponent={MasterVehicleAddEdit}
              />
            </Box>
            <Box sx={{ flex: '0 1 180px' }}>
              <FormControl fullWidth size="medium">
                <InputLabel id="period-label">{t('reports.selectPeriod', 'Pilih Periode')}</InputLabel>
                <MuiSelect
                  labelId="period-label"
                  value={periodType}
                  onChange={(e) => setPeriodType(e.target.value)}
                  disabled={loading}
                  input={<OutlinedInput label={t('reports.selectPeriod', 'Pilih Periode')} />}
                  sx={{ bgcolor: 'background.paper' }}
                >
                  <MenuItem value="today">{t('mapsTracking.today', 'Today')}</MenuItem>
                  <MenuItem value="week">{t('time.lastWeek', 'Last Week')}</MenuItem>
                  <MenuItem value="month">{t('time.lastMonth', 'Last Month')}</MenuItem>
                  <MenuItem value="custom">{t('reports.custom', 'Custom')}</MenuItem>
                </MuiSelect>
              </FormControl>
            </Box>

            {periodType === 'custom' && (
              <Box sx={{ flex: '1 1 350px' }}>
                <Stack direction="row" spacing={2}>
                  <DateTimePicker
                    views={['year', 'month', 'day', 'hours', 'minutes']}
                    ampm={false}
                    label={t('reports.from', 'From')}
                    timeSteps={{ minutes: 1 }}
                    format="DD/MM/YYYY HH:mm"
                    value={fromDate ? dayjs(fromDate) : null}
                    onChange={(v) => {
                      const newV = v ? v.second(0).format('YYYY-MM-DD HH:mm:ss') : '';
                      setFromDate(newV);
                      validateDateRange(dayjs(newV), dayjs(toDate));
                    }}
                    maxDate={toDate ? dayjs(toDate) : undefined}
                    disabled={loading}
                    slotProps={{ textField: { size: 'small', fullWidth: true, error: !!dateError, sx: { bgcolor: 'background.paper' } } }}
                  />
                  <DateTimePicker
                    views={['year', 'month', 'day', 'hours', 'minutes']}
                    ampm={false}
                    label={t('reports.to', 'To')}
                    timeSteps={{ minutes: 1 }}
                    format="DD/MM/YYYY HH:mm"
                    value={toDate ? dayjs(toDate) : null}
                    onChange={(v) => {
                      const newV = v ? v.second(59).format('YYYY-MM-DD HH:mm:ss') : '';
                      setToDate(newV);
                      validateDateRange(dayjs(fromDate), dayjs(newV));
                    }}
                    minDate={fromDate ? dayjs(fromDate) : undefined}
                    disabled={loading}
                    slotProps={{ textField: { size: 'small', fullWidth: true, error: !!dateError, sx: { bgcolor: 'background.paper' } } }}
                  />
                </Stack>
              </Box>
            )}

            <Box sx={{ flex: '0 0 auto', ml: 'auto', display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleFetchData}
                disabled={loading || !!dateError}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                sx={{
                  height: 40,
                  px: 2,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 'bold',
                  boxShadow: 'none'
                }}
              >
                {t('general.search', 'Search')}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={(e) => setExportAnchorEl(e.currentTarget)}
                disabled={loading || reportData.length === 0}
                startIcon={<FileDownloadIcon />}
                sx={{ height: 40, px: 2, borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
              >
                {t('general.export', 'Export')}
              </Button>
              <Menu
                anchorEl={exportAnchorEl}
                open={openExportMenu}
                onClose={() => setExportAnchorEl(null)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right'
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
              >
                <MenuItem
                  onClick={() => {
                    setExportAnchorEl(null);
                    handleExport(false);
                  }}
                >
                  {t('revenue.exportExcel', 'Export Excel')}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setExportAnchorEl(null);
                    handleExport(true);
                  }}
                >
                  {t('reportModule.exportCsv', 'Export CSV')}
                </MenuItem>
              </Menu>
            </Box>
          </Box>

          {loading && (
            <Box sx={{ width: '100%', mt: 2 }}>
              <Typography variant="body2" color="text.secondary" align="center" gutterBottom>
                {loadingText || t('buttonTitle.loading', 'Loading...')} ({loadingProgress}%)
              </Typography>
              <LinearProgress variant="determinate" value={loadingProgress} />
            </Box>
          )}
        </Paper>

        <Paper
          elevation={0}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minHeight: 200,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper'
          }}
        >
          {generationTimeRange && (
            <Box sx={{ p: 1.5, flexShrink: 0, borderBottom: '1px solid #e0e0e0', backgroundColor: '#fafafa' }}>
              <Typography variant="subtitle2" color="text.secondary">
                {t('reportModule.showDataFrom', 'Show data from:')} <strong>{displayStartDate}</strong> {t('revenue.to', 'to')}{' '}
                <strong>{displayEndDate}</strong>
              </Typography>
            </Box>
          )}

          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <TableContainer sx={{ height: '100%', overflowX: 'auto', overflowY: 'auto' }}>
              <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%', minWidth: 2000 }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                  {/* ROW 1 */}
                  <tr>
                    <th rowSpan={3} style={{ ...headerStyle, position: 'sticky', left: 0, zIndex: 3, minWidth: 120, width: 120 }}>
                      {t('reports.licensePlate', 'License Plate')}
                    </th>
                    <th colSpan={4} style={headerStyle}>
                      {t('reportModule.travelTime', 'Travel Time')}
                    </th>
                    <th rowSpan={3} style={headerStyle}>
                      {t('reportModule.idle', 'Idle')}
                    </th>
                    <th colSpan={2} style={headerStyle}>
                      {t('exportWaypoint.engine', 'Engine')}
                    </th>
                    <th rowSpan={3} style={headerStyle}>
                      {t('reportModule.canbusEngineWorkTime', 'Canbus Engine Work Time')}
                      <br />
                      (hh:mm:ss)
                    </th>
                    <th colSpan={3} style={headerStyle}>
                      {t('reports.speed', 'Speed')}
                    </th>
                    <th rowSpan={3} style={headerStyle}>
                      {t('reportModule.fuelTank', 'Fuel Tank')}
                    </th>
                    <th rowSpan={3} style={headerStyle}>
                      {t('reportModule.ratioLKm', 'Ratio (L:km)')}
                    </th>
                    <th colSpan={2} style={headerStyle}>
                      {t('reportModule.fuelConsumptionByRatio', 'Fuel Consumption By Ratio')}
                    </th>
                    <th colSpan={8} style={headerStyle}>
                      {t('reportModule.fuelSensor', 'Fuel Sensor')}
                    </th>
                    <th colSpan={8} style={headerStyle}>
                      {t('reportModule.canbusFuel', 'Canbus Fuel')}
                    </th>
                  </tr>

                  {/* ROW 2 */}
                  <tr>
                    <th rowSpan={2} style={headerStyle}>
                      {t('reportModule.startKm', 'Start (Km)')}
                    </th>
                    <th rowSpan={2} style={headerStyle}>
                      {t('reportModule.endKm', 'End (km)')}
                    </th>
                    <th rowSpan={2} style={headerStyle}>
                      {t('reportModule.distanceKm', 'Distance (km)')}
                    </th>
                    <th rowSpan={2} style={headerStyle}>
                      {t('reportModule.durationHhMmSs', 'Duration (hh:mm:ss)')}
                    </th>

                    <th rowSpan={2} style={headerStyle}>
                      {t('reportModule.onHhMmSs', 'ON (hh:mm:ss)')}
                    </th>
                    <th rowSpan={2} style={headerStyle}>
                      {t('reportModule.offHhMmSs', 'OFF (hh:mm:ss)')}
                    </th>

                    <th rowSpan={2} style={headerStyle}>
                      {t('reportModule.overTime', 'Over (time)')}
                    </th>
                    <th rowSpan={2} style={headerStyle}>
                      {t('reportModule.maxKmH', 'Max (km/h)')}
                    </th>
                    <th rowSpan={2} style={headerStyle}>
                      {t('reportModule.avgKmH', 'Avg (km/h)')}
                    </th>

                    <th rowSpan={2} style={headerStyle}>
                      %
                    </th>
                    <th rowSpan={2} style={headerStyle}>
                      {t('reportModule.litre', 'Litre')}
                    </th>

                    <th colSpan={2} style={headerStyle}>
                      {t('time.start', 'Start')}
                    </th>
                    <th colSpan={2} style={headerStyle}>
                      {t('time.end', 'End')}
                    </th>
                    <th colSpan={2} style={headerStyle}>
                      {t('dailyFuel.refueling', 'Refueling')}
                    </th>
                    <th colSpan={2} style={headerStyle}>
                      {t('reportModule.consumption', 'Consumption')}
                    </th>

                    <th colSpan={2} style={headerStyle}>
                      {t('time.start', 'Start')}
                    </th>
                    <th colSpan={2} style={headerStyle}>
                      {t('time.end', 'End')}
                    </th>
                    <th colSpan={2} style={headerStyle}>
                      {t('dailyFuel.refueling', 'Refueling')}
                    </th>
                    <th colSpan={2} style={headerStyle}>
                      {t('reportModule.consumption', 'Consumption')}
                    </th>
                  </tr>

                  {/* ROW 3 */}
                  <tr>
                    {/* Fuel Sensor */}
                    <th style={headerStyle}>%</th>
                    <th style={headerStyle}>{t('reportModule.litre', 'Litre')}</th>
                    <th style={headerStyle}>%</th>
                    <th style={headerStyle}>{t('reportModule.litre', 'Litre')}</th>
                    <th style={headerStyle}>%</th>
                    <th style={headerStyle}>{t('reportModule.litre', 'Litre')}</th>
                    <th style={headerStyle}>%</th>
                    <th style={headerStyle}>{t('reportModule.litre', 'Litre')}</th>

                    {/* Canbus Fuel */}
                    <th style={headerStyle}>%</th>
                    <th style={headerStyle}>{t('reportModule.litre', 'Litre')}</th>
                    <th style={headerStyle}>%</th>
                    <th style={headerStyle}>{t('reportModule.litre', 'Litre')}</th>
                    <th style={headerStyle}>%</th>
                    <th style={headerStyle}>{t('reportModule.litre', 'Litre')}</th>
                    <th style={headerStyle}>%</th>
                    <th style={headerStyle}>{t('reportModule.litre', 'Litre')}</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData && reportData.length > 0 ? (
                    reportData
                      .slice(
                        paginationModel.page * paginationModel.pageSize,
                        paginationModel.page * paginationModel.pageSize + paginationModel.pageSize
                      )
                      .map((row, index) => (
                        <tr key={index}>
                          <td
                            style={{
                              ...cellStyle,
                              position: 'sticky',
                              left: 0,
                              backgroundColor: '#fff',
                              zIndex: 1,
                              fontWeight: 'bold',
                              minWidth: 120,
                              width: 120
                            }}
                          >
                            {row?.vehicle?.license_plate || '-'}
                          </td>
                          <td style={cellStyle}>{row?.start_odo != null ? formatNum(row.start_odo / 1000) : '-'}</td>
                          <td style={cellStyle}>{row?.end_odo != null ? formatNum(row.end_odo / 1000) : '-'}</td>
                          <td style={cellStyle}>{formatNum(row?.total_distance)}</td>
                          <td style={cellStyle}>{row?.duration ?? '-'}</td>

                          <td style={cellStyle}>{row?.duration_engine_idle ?? '-'}</td>

                          <td style={cellStyle}>{row?.duration_engine_on ?? '-'}</td>
                          <td style={cellStyle}>{row?.duration_engine_off ?? '-'}</td>

                          <td style={cellStyle}>{row?.can_engine_worktime ?? '-'}</td>

                          <td style={cellStyle}>{row?.total_over_speed ?? '-'}</td>
                          <td style={cellStyle}>{formatNum(row?.max_speed)}</td>
                          <td style={cellStyle}>{formatNum(row?.avg_speed)}</td>

                          <td style={cellStyle}>{row?.fuel_tank ?? '-'}</td>

                          <td style={cellStyle}>{row?.ratio_litre_to_km ?? '-'}</td>

                          <td style={cellStyle}>{formatNum(row?.fuel_consumption_by_ratio_percentage)}</td>
                          <td style={cellStyle}>{formatNum(row?.fuel_consumption_by_ratio_litre)}</td>

                          <td style={cellStyle}>{formatNum(row?.fuel_start_percentage)}</td>
                          <td style={cellStyle}>{formatNum(row?.fuel_start_litre)}</td>
                          <td style={cellStyle}>{formatNum(row?.fuel_end_percentage)}</td>
                          <td style={cellStyle}>{formatNum(row?.fuel_end_litre)}</td>
                          <td style={cellStyle}>{formatNum(row?.refuel_percentage)}</td>
                          <td style={cellStyle}>{formatNum(row?.refuel_litre)}</td>
                          <td style={cellStyle}>{formatNum(row?.fuel_consumption_percentage)}</td>
                          <td style={cellStyle}>{formatNum(row?.fuel_consumption_litre)}</td>

                          <td style={cellStyle}>{formatNum(row?.canbus_fuel_start_percentage)}</td>
                          <td style={cellStyle}>{formatNum(row?.canbus_fuel_start_litre)}</td>
                          <td style={cellStyle}>{formatNum(row?.canbus_fuel_end_percentage)}</td>
                          <td style={cellStyle}>{formatNum(row?.canbus_fuel_end_litre)}</td>
                          <td style={cellStyle}>{formatNum(row?.canbus_refuel_percentage)}</td>
                          <td style={cellStyle}>{formatNum(row?.canbus_refuel_litre)}</td>
                          <td style={cellStyle}>{formatNum(row?.canbus_fuel_consumption_percentage)}</td>
                          <td style={cellStyle}>{formatNum(row?.canbus_fuel_consumption_litre)}</td>
                        </tr>
                      ))
                  ) : (
                    <tr>
                      <td colSpan={32} style={{ ...cellStyle, textAlign: 'center', padding: '20px' }}>
                        {!loading
                          ? t(
                              'reportModule.noDataAvailablePleaseSelectFilters',
                              'No data available. Please select filters and click Search.'
                            )
                          : t('general.loading', 'Loading data...')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </TableContainer>
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'flex-end',
              borderTop: '1px solid #e0e0e0',
              bgcolor: 'background.paper',
              zIndex: 1,
              pt: 0.5
            }}
          >
            <TablePagination
              component="div"
              count={reportData.length}
              page={paginationModel.page}
              onPageChange={(e, newPage) => setPaginationModel((prev) => ({ ...prev, page: newPage }))}
              rowsPerPage={paginationModel.pageSize}
              onRowsPerPageChange={(e) => setPaginationModel({ page: 0, pageSize: parseInt(e.target.value, 10) })}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{
                '& .MuiTablePagination-toolbar': {
                  minHeight: 48,
                  px: 1
                }
              }}
            />
          </Box>
        </Paper>
      </Box>
    </LocalizationProvider>
  );
}
